import {
  Table,
  CoursesIcon,
  Dropdown,
  Button,
  DotsVerticalIcon,
  DropdownMenu,
  DropdownMenuItem,
  Row,
  useDefaultSorting,
} from '@bp/ui-components';
import { CourseTableType } from '../types/types';
import { FragmentType } from 'client/gql';
import { BpPermissionChecker } from 'utils/PermissionChecker';
import { UseCoursesFragment, useCourses } from '../hooks/useCourses';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useTranslation } from 'react-i18next';

type CooperationCoursesTableProps = {
  query: FragmentType<typeof UseCoursesFragment> | undefined;
  permissions: BpPermissionChecker | null;
  onNavigate: (uuid: string) => void;
  onProfilesEdit: (courseUuid: string) => void;
};

export const CooperationCoursesTable = ({
  query,
  permissions,
  onNavigate,
  onProfilesEdit,
}: CooperationCoursesTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();

  const { data, columns } = useCourses({
    query,
    columns: ['name', 'organization', 'admins', 'schoolYear', 'nextcloud', 'threema'],
    onNavigate,
  });

  const { sorting, onSortingChange } = useDefaultSorting();

  // for Collaboration Groups only Members are editable
  const canEditProfiles = (row: Row<CourseTableType>) =>
    permissions?.canUpdateCourse({
      uuid: row.original.uuid,
      organization: { uuid: pimAuthClaims.getOrganizationUuid() },
    }) && row.original.organizationUuid !== pimAuthClaims.getOrganizationUuid();

  function getDropdownMenuItems(row: Row<CourseTableType>): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [];

    if (canEditProfiles(row)) {
      items.push({
        label: t('common.editProfiles'),
        onClick: () => onProfilesEdit(row.original.uuid),
      });
    }

    return items;
  }

  return (
    <Table<CourseTableType>
      breakpoint='580px'
      data-cy='courseList'
      showActionBar
      isOnWhite={false}
      showBorderRadius
      showShadow
      showSort
      canScroll
      sorting={sorting}
      onSortingChange={onSortingChange}
      actionBarSettings={{
        showExpertFilter: true,
      }}
      emptyStateSettings={{
        icon: <CoursesIcon />,
        padding: 'xl',
      }}
      columns={columns}
      data={data}
      lastColWidth='32px'
      lastCol={(row) => {
        const items = getDropdownMenuItems(row);
        const disabled = items.length === 0;
        return (
          <Dropdown
            disabled={disabled}
            trigger={<Button disabled={disabled} hierarchy='ghost' icon={<DotsVerticalIcon />} />}
          >
            <DropdownMenu data={items} />
          </Dropdown>
        );
      }}
    />
  );
};
